import React, { useState } from 'react';
import { View, Text, Pressable, TextInput } from 'react-native';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { Comment, CommentReactionType } from '../../shared/types/comments';
import { useCommentStore } from '../../store/commentStore';
import { useAuthStore } from '../../store/authStore';
import { Button, TextButton } from '../ui';
import { decodeHtmlEntities } from '../../shared/utils/html-entities';
import { addColorOpacity } from '../../shared/utils/styles';
import { getDisplayName, getInitials } from '../../shared/utils/user';
import { Ionicons } from '@expo/vector-icons';
import { logger } from '../../shared/utils/logger';
import { REACTION_EMOJIS } from '../../shared/config/reactions';

// Use centralized reaction config
const REACTION_TYPES = REACTION_EMOJIS;

interface CommentItemProps {
  comment: Comment;
  depth?: number;
  onReply?: (parentId: number) => void;
  index?: number; // For alternating backgrounds
  isLastInGroup?: boolean; // For visual separation
}

export function CommentItem({ comment, depth = 0, onReply, index = 0, isLastInGroup = false }: CommentItemProps) {
  const styles = useStyles();
  const responsive = useResponsive();
  const { isMobile, isSmallMobile } = responsive;
  const user = useAuthStore((state) => state.user);
  const { addReaction, removeReaction, deleteComment, updateComment } = useCommentStore();
  
  const [showReactions, setShowReactions] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const isOwnComment = user?.id === comment.created_by;
  const maxDepth = 3; // Limit nesting depth
  
  // Alternating background: even index gets subtle background, odd gets transparent
  // Own comments get a slightly different tint (like Slack highlights your own messages)
  // This improves readability and visual separation between messages
  const getBackgroundColor = () => {
    if (isOwnComment) {
      // Own messages get a subtle primary tint
      return index % 2 === 0
        ? addColorOpacity(styles.tokens.color.primary, 0.04)
        : addColorOpacity(styles.tokens.color.primary, 0.06);
    }
    // Other messages alternate between subtle gray and transparent
    return index % 2 === 0
      ? addColorOpacity(styles.tokens.color.onSurface, 0.025)
      : 'transparent';
  };

  const handleReaction = async (reactionType: CommentReactionType) => {
    try {
      const hasReaction = comment.reactions?.some(
        (r) => r.user_id === user?.id && r.reaction_type === reactionType
      );

      if (hasReaction) {
        await removeReaction(comment.id, reactionType);
      } else {
        await addReaction(comment.id, reactionType);
      }
    } catch (error) {
      logger.error('Error toggling reaction', error, 'comments');
    }
  };

  const handleDelete = async () => {
    if (!isOwnComment) return;
    
    setIsDeleting(true);
    try {
      await deleteComment(comment.id);
    } catch (error) {
      logger.error('Error deleting comment', error, 'comments');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      await updateComment(comment.id, { content: editContent });
      setIsEditing(false);
    } catch (error) {
      logger.error('Error updating comment', error, 'comments');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (comment.deleted_at) {
    return (
      <View
        style={{
          paddingVertical: isSmallMobile ? styles.utils.spacing.xs : styles.utils.spacing.sm,
          paddingHorizontal: isSmallMobile ? styles.utils.spacing.sm : styles.utils.spacing.md,
          marginLeft: depth > 0 ? (isSmallMobile ? styles.utils.spacing.md : styles.utils.spacing.xl) : 0,
        }}
      >
        <Text
          style={{
            fontSize: isSmallMobile ? styles.utils.typography.sizes.xs : styles.utils.typography.sizes.sm,
            color: addColorOpacity(styles.tokens.color.onSurface, 0.5),
            fontStyle: 'italic',
            fontFamily: styles.utils.typography.body.fontFamily,
          }}
        >
          Comment deleted
        </Text>
      </View>
    );
  }

  const displayName = getDisplayName(comment.created_by_user);
  const initials = getInitials(comment.created_by_user);
  const isReply = depth > 0;

  return (
    <View
      style={{
        paddingVertical: isSmallMobile ? styles.utils.spacing.xs + 2 : styles.utils.spacing.sm + 2,
        paddingHorizontal: isSmallMobile ? styles.utils.spacing.sm : styles.utils.spacing.md,
        marginLeft: isReply ? (isSmallMobile ? styles.utils.spacing.md : styles.utils.spacing.xl) : 0,
        borderLeftWidth: isReply ? 2 : 0,
        borderLeftColor: isReply ? addColorOpacity(styles.tokens.color.primary, 0.25) : 'transparent',
        paddingLeft: isReply ? (isSmallMobile ? styles.utils.spacing.sm + 2 : styles.utils.spacing.md + 4) : (isSmallMobile ? styles.utils.spacing.sm : styles.utils.spacing.md),
        backgroundColor: isHovered
          ? addColorOpacity(styles.tokens.color.primary, 0.08)
          : getBackgroundColor(),
        borderBottomWidth: isLastInGroup && !isReply ? 1 : 0,
        borderBottomColor: isLastInGroup && !isReply
          ? addColorOpacity(styles.tokens.color.border, 0.2)
          : 'transparent',
        marginBottom: isLastInGroup && !isReply ? (isSmallMobile ? styles.utils.spacing.sm : styles.utils.spacing.md) : 0,
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: isSmallMobile ? styles.utils.spacing.xs : styles.utils.spacing.sm }}>
        {/* Avatar */}
        <View
          style={{
            width: isSmallMobile ? 26 : 32,
            height: isSmallMobile ? 26 : 32,
            borderRadius: isSmallMobile ? 13 : 16,
            backgroundColor: styles.tokens.color.primary,
            justifyContent: 'center',
            alignItems: 'center',
            flexShrink: 0,
            marginTop: 2,
          }}
        >
          <Text
            style={{
              fontSize: isSmallMobile ? 10 : styles.utils.typography.sizes.xs,
              fontWeight: styles.utils.typography.weights.bold,
              color: styles.tokens.color.onPrimary,
              fontFamily: styles.utils.typography.body.fontFamily,
            }}
          >
            {initials}
          </Text>
        </View>

        {/* Message Body */}
        <View style={{ flex: 1, minWidth: 0 }}>
          {/* Header: Name and Timestamp */}
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: isSmallMobile ? 4 : styles.utils.spacing.xs, marginBottom: isSmallMobile ? 1 : 2, flexWrap: 'wrap' }}>
            <Text
              style={{
                fontSize: isSmallMobile ? styles.utils.typography.sizes.xs : styles.utils.typography.sizes.sm,
                fontWeight: styles.utils.typography.weights.semibold,
                color: styles.tokens.color.onSurface,
                fontFamily: styles.utils.typography.body.fontFamily,
              }}
              numberOfLines={1}
            >
              {displayName}
            </Text>
            <Text
              style={{
                fontSize: isSmallMobile ? 10 : styles.utils.typography.sizes.xs,
                color: addColorOpacity(styles.tokens.color.onSurface, 0.5),
                fontFamily: styles.utils.typography.body.fontFamily,
              }}
            >
              {formatDate(comment.created_at)}
              {comment.edited_at && ' (edited)'}
            </Text>
          </View>

          {/* Content */}
          {isEditing ? (
            <View style={{ marginTop: styles.utils.spacing.xs }}>
              <TextInput
                style={{
                  borderWidth: 1,
                  borderColor: styles.tokens.color.border,
                  borderRadius: styles.utils.radius.md,
                  padding: isSmallMobile ? styles.utils.spacing.xs : styles.utils.spacing.sm,
                  backgroundColor: styles.tokens.color.surface,
                  color: styles.tokens.color.onSurface,
                  fontSize: isSmallMobile ? styles.utils.typography.sizes.sm : styles.utils.typography.sizes.base,
                  fontFamily: styles.utils.typography.body.fontFamily,
                  minHeight: isSmallMobile ? 60 : 80,
                  textAlignVertical: 'top',
                  marginBottom: isSmallMobile ? styles.utils.spacing.xs : styles.utils.spacing.sm,
                }}
                value={editContent}
                onChangeText={setEditContent}
                multiline
                placeholderTextColor={addColorOpacity(styles.tokens.color.onSurface, 0.5)}
                spellCheck={true}
                autoCorrect={true}
                autoCapitalize="sentences"
              />
              <View style={{ flexDirection: 'row', gap: isSmallMobile ? styles.utils.spacing.xs : styles.utils.spacing.sm, justifyContent: 'flex-end' }}>
                <Button
                  title="Cancel"
                  variant="outline"
                  onPress={() => {
                    setIsEditing(false);
                    setEditContent(comment.content);
                  }}
                />
                <Button
                  title="Save"
                  variant="primary"
                  onPress={handleSaveEdit}
                  disabled={!editContent.trim()}
                />
              </View>
            </View>
          ) : (
            <Text
              style={{
                fontSize: isSmallMobile ? styles.utils.typography.sizes.sm : styles.utils.typography.sizes.base,
                color: styles.tokens.color.onSurface,
                fontFamily: styles.utils.typography.body.fontFamily,
                lineHeight: (isSmallMobile ? styles.utils.typography.sizes.sm : styles.utils.typography.sizes.base) * 1.5,
                marginTop: isSmallMobile ? 1 : 2,
              }}
            >
              {decodeHtmlEntities(comment.content)}
            </Text>
          )}

          {/* Reactions */}
          {comment.reactions && comment.reactions.length > 0 && (
            <View
              style={{
                flexDirection: 'row',
                gap: isSmallMobile ? 2 : styles.utils.spacing.xs,
                marginTop: isSmallMobile ? 2 : styles.utils.spacing.xs,
                flexWrap: 'wrap',
              }}
            >
              {REACTION_TYPES.map((reactionType) => {
                const reactions = comment.reactions?.filter(
                  (r) => r.reaction_type === reactionType
                ) || [];
                if (reactions.length === 0) return null;
                const hasUserReaction = reactions.some((r) => r.user_id === user?.id);
                return (
                  <Pressable
                    key={reactionType}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: isSmallMobile ? 2 : 4,
                      paddingHorizontal: isSmallMobile ? 4 : styles.utils.spacing.xs,
                      paddingVertical: isSmallMobile ? 1 : 2,
                      backgroundColor: hasUserReaction
                        ? addColorOpacity(styles.tokens.color.primary, 0.15)
                        : addColorOpacity(styles.tokens.color.onSurface, 0.05),
                      borderRadius: styles.utils.radius.full,
                      borderWidth: hasUserReaction ? 1 : 0,
                      borderColor: hasUserReaction ? styles.tokens.color.primary : 'transparent',
                    }}
                    onPress={() => handleReaction(reactionType)}
                  >
                    <Text style={{ fontSize: isSmallMobile ? 12 : 14 }}>{reactionType}</Text>
                    <Text
                      style={{
                        fontSize: isSmallMobile ? 10 : styles.utils.typography.sizes.xs,
                        color: hasUserReaction
                          ? styles.tokens.color.primary
                          : addColorOpacity(styles.tokens.color.onSurface, 0.7),
                        fontWeight: styles.utils.typography.weights.medium,
                        fontFamily: styles.utils.typography.body.fontFamily,
                      }}
                    >
                      {reactions.length}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          )}

          {/* Actions */}
          {!isEditing && (
            <View
              style={{
                flexDirection: 'row',
                gap: isSmallMobile ? styles.utils.spacing.sm : styles.utils.spacing.md,
                marginTop: isSmallMobile ? 2 : styles.utils.spacing.xs,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <TextButton
                title="React"
                onPress={() => setShowReactions(!showReactions)}
                variant="primary"
                size="sm"
              />

              {depth < maxDepth && onReply && (
                <TextButton
                  title="Reply"
                  onPress={() => onReply(comment.id)}
                  variant="primary"
                  size="sm"
                />
              )}

              {isOwnComment && (
                <>
                  <TextButton
                    title="Edit"
                    onPress={() => {
                      setIsEditing(true);
                      setEditContent(comment.content);
                    }}
                    variant="primary"
                    size="sm"
                  />
                  <TextButton
                    title="Delete"
                    onPress={handleDelete}
                    variant="danger"
                    size="sm"
                    disabled={isDeleting}
                  />
                </>
              )}
            </View>
          )}

          {/* Reaction Picker */}
          {showReactions && (
            <View
              style={{
                flexDirection: 'row',
                gap: isSmallMobile ? 2 : styles.utils.spacing.xs,
                marginTop: isSmallMobile ? styles.utils.spacing.xs : styles.utils.spacing.sm,
                padding: isSmallMobile ? 4 : styles.utils.spacing.xs,
                backgroundColor: styles.tokens.color.surface,
                borderRadius: styles.utils.radius.md,
                borderWidth: 1,
                borderColor: styles.tokens.color.border,
                flexWrap: 'wrap',
              }}
            >
              {REACTION_TYPES.map((reactionType) => {
                const hasReaction = comment.reactions?.some(
                  (r) => r.user_id === user?.id && r.reaction_type === reactionType
                );
                return (
                  <Pressable
                    key={reactionType}
                    style={{
                      padding: isSmallMobile ? 4 : styles.utils.spacing.xs,
                      borderRadius: styles.utils.radius.sm,
                      backgroundColor: hasReaction
                        ? addColorOpacity(styles.tokens.color.primary, 0.1)
                        : 'transparent',
                    }}
                    onPress={() => {
                      handleReaction(reactionType);
                      setShowReactions(false);
                    }}
                  >
                    <Text style={{ fontSize: isSmallMobile ? 16 : 20 }}>{reactionType}</Text>
                  </Pressable>
                );
              })}
            </View>
          )}
        </View>
      </View>

      {/* Replies */}
      {comment.replies && comment.replies.length > 0 && (
        <View style={{ marginTop: isSmallMobile ? 2 : styles.utils.spacing.xs }}>
          {comment.replies.map((reply, replyIndex) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              depth={depth + 1}
              onReply={onReply}
              index={replyIndex}
              isLastInGroup={replyIndex === comment.replies!.length - 1}
            />
          ))}
        </View>
      )}
    </View>
  );
}

